import { useFormikContext } from "formik";
import React, { useEffect, useState } from "react";
// Images
import uploadImage from "../../assets/images/upload.svg";
import { useTranslation } from "react-i18next";
import ImageFilePreview from "../../components/ImageFilePreview";
import { toast } from "react-toastify";
import PreviewModal from "../../components/PreviewModal";
import document_img from "../../assets/images/document-img.jpg";

const Step3 = () => {
  const { t } = useTranslation();
  const { handleChange, handleBlur, values, touched, errors, setFieldValue } =
    useFormikContext();


  const [commercialRegistrationNumberAttachmentType, setCommercialRegistrationNumberAttachmentType] = useState(null);
  const [commercialRegistrationNumberAttachments, setCommercialRegistrationNumberAttachments] = useState(null);
  const [taxIdentifictionNumberAttachmentType, setTaxIdentifictionNumberAttachmentType] = useState(null);
  const [taxIdentifictionNumberAttachments, setTaxIdentifictionNumberAttachments] = useState(null);


  const [previewModal, setPreviewModal] = useState({
    show: false,
    title: "",
    fileUrl: "",
    fileType: "",
  });

  const getFileTypeFromUrl = (url) => {
    if (!url || typeof url !== "string") return "";
    const ext = url.split('.').pop().toLowerCase();
    if (["png", "jpg", "jpeg"].includes(ext)) return "image";
    if (ext === "pdf") return "pdf";
    if (ext === "doc" || ext === "docx") return "doc";
    return "";
  };

  const handleViewDocument = (title, attachment, attachmentType, formikValue) => {
    let fileUrl = "";
    let fileType = "";
    if (attachment && typeof attachment === "string") {
      fileUrl = attachment;
      fileType = attachmentType?.startsWith("image/")
        ? "image"
        : attachmentType?.includes("pdf")
          ? "pdf"
          : attachmentType?.includes("word") || attachmentType?.includes("doc")
            ? "doc"
            : "";
    } else if (formikValue) {
      // If formikValue is a File object, create an object URL
      if (formikValue instanceof File) {
        fileUrl = URL.createObjectURL(formikValue);
        fileType = formikValue.type?.startsWith("image/")
          ? "image"
          : formikValue.type?.includes("pdf")
            ? "pdf"
            : formikValue.name?.toLowerCase().endsWith(".doc") || formikValue.name?.toLowerCase().endsWith(".docx")
              ? "doc"
              : "";
      } else if (typeof formikValue === "string") {
        fileUrl = formikValue;
        fileType = getFileTypeFromUrl(fileUrl);
      }
    }

    // DOC/DOCX open in new tab
    if (fileType === "doc") {
      const officeUrl = `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(fileUrl)}`;
      window.open(officeUrl, "_blank", "noopener,noreferrer");
    } else {
      setPreviewModal({
        show: true,
        title,
        fileUrl,
        fileType,
      });
    }
  };

  // Re-initialize preview state from Formik File objects when component mounts
  useEffect(() => {
    // Commercial Registration Document
    const commercialFile = values?.commercialRegistrationNumberAttachment;
    if (commercialFile instanceof File && !commercialRegistrationNumberAttachments) {
      const fileType = commercialFile.type;
      setCommercialRegistrationNumberAttachmentType(fileType);
      if (fileType?.startsWith("image/")) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setCommercialRegistrationNumberAttachments(reader.result);
        };
        reader.readAsDataURL(commercialFile);
      } else {
        const fileURL = URL.createObjectURL(commercialFile);
        setCommercialRegistrationNumberAttachments(fileURL);
      }
    }

    // Tax Identification Document
    const taxFile = values?.taxIdentifictionNumberAttachment;
    if (taxFile instanceof File && !taxIdentifictionNumberAttachments) {
      const fileType = taxFile.type;
      setTaxIdentifictionNumberAttachmentType(fileType);
      if (fileType?.startsWith("image/")) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setTaxIdentifictionNumberAttachments(reader.result);
        };
        reader.readAsDataURL(taxFile);
      } else {
        const fileURL = URL.createObjectURL(taxFile);
        setTaxIdentifictionNumberAttachments(fileURL);
      }
    }
  }, [values?.commercialRegistrationNumberAttachment, values?.taxIdentifictionNumberAttachment]);

  const handleRemoveCommercialDocument = () => {
    setCommercialRegistrationNumberAttachments(null);
    setCommercialRegistrationNumberAttachmentType(null);
    setFieldValue("commercialRegistrationNumberAttachment", null);
    // Clear the file input
    const fileInput = document.getElementById("commercialRegistrationNumberAttachment");
    if (fileInput) fileInput.value = "";
  };

  const handleRemoveTaxDocument = () => {
    setTaxIdentifictionNumberAttachments(null);
    setTaxIdentifictionNumberAttachmentType(null);
    setFieldValue("taxIdentifictionNumberAttachment", null);
    // Clear the file input
    const fileInput = document.getElementById("taxIdentifictionNumberAttachment");
    if (fileInput) fileInput.value = "";
  };

  const handleCommercialRegistrationAttachmentChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = [
      "image/jpeg",
      "image/png",
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];
    const allowedExtensions = [".jpg", ".jpeg", ".png", ".pdf", ".doc", ".docx"];

    const fileTypeValid = allowedTypes.includes(file.type);
    const fileExtensionValid = allowedExtensions.some((ext) =>
      file.name.toLowerCase().endsWith(ext)
    );

    if (!fileTypeValid || !fileExtensionValid) {
      toast.error(
        t("Invalid file type. Supported formats: .png, .jpg, .pdf, .doc, .docx.")
      );
      e.target.value = "";
      return;
    }

    if (file) {
      const fileType = file.type;
      setCommercialRegistrationNumberAttachmentType(fileType);

      // Store object URL for preview
      if (fileType.startsWith("image/")) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setCommercialRegistrationNumberAttachments(reader.result); // Base64 image
        };
        reader.readAsDataURL(file);
      } else {
        const fileURL = URL.createObjectURL(file);
        setCommercialRegistrationNumberAttachments(fileURL); // Object URL for PDF/DOC
      }

      setFieldValue("commercialRegistrationNumberAttachment", file);
    }
  };

  const handleTaxIdentificationAttachmentChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = [
      "image/jpeg",
      "image/png",
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];

    const allowedExtensions = [".jpg", ".jpeg", ".png", ".pdf", ".doc", ".docx"];

    const fileTypeValid = allowedTypes.includes(file.type);
    const fileExtensionValid = allowedExtensions.some((ext) =>
      file.name.toLowerCase().endsWith(ext)
    );

    if (!fileTypeValid || !fileExtensionValid) {
      toast.error(
        t("Invalid file type. Supported formats: .png, .jpg, .pdf, .doc, .docx.")
      );
      e.target.value = "";
      return;
    }

    if (file) {
      const fileType = file.type;
      setTaxIdentifictionNumberAttachmentType(fileType);

      // Store object URL for preview
      if (fileType.startsWith("image/")) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setTaxIdentifictionNumberAttachments(reader.result); // Base64 image
        };
        reader.readAsDataURL(file);
      } else {
        const fileURL = URL.createObjectURL(file);
        setTaxIdentifictionNumberAttachments(fileURL); // Object URL for PDF/DOC
      }

      setFieldValue("taxIdentifictionNumberAttachment", file);
    }
  };

  return (
    <>
      <div className="mb-3">
        <input
          type="text"
          className="form-control"
          name="commercialRegistrationNumber"
          placeholder={`${t("Commercial Registration number")} *`}
          required
          onChange={handleChange}
          value={values.commercialRegistrationNumber}
          onBlur={handleBlur}
        />
        {touched.commercialRegistrationNumber &&
          errors.commercialRegistrationNumber && (
            <div className="text-danger">
              {errors.commercialRegistrationNumber}
            </div>
          )}
      </div>

      <div className="upload-container d-flex align-items-center mb-3">
        <div
          className="upload-box"
          onClick={() =>
            document
              .getElementById("commercialRegistrationNumberAttachment")
              .click()
          }
        >

          {values?.commercialRegistrationNumberAttachment && !commercialRegistrationNumberAttachments ? (
            <img
              src={values?.commercialRegistrationNumberAttachment}
              alt={t("Upload Icon")}
              className="upload-icon"
            />
          ) : commercialRegistrationNumberAttachmentType && !commercialRegistrationNumberAttachmentType.startsWith("image/") ? (
            <a href={commercialRegistrationNumberAttachments} target="_blank" rel="noopener noreferrer">
              <img
                src={document_img}
                alt="Document Icon"
                className="upload-icon"
                style={{ cursor: "pointer" }}
              />
            </a>
          ) : (
            <img
              src={commercialRegistrationNumberAttachments ? commercialRegistrationNumberAttachments : uploadImage}
              alt={t("Upload Icon")}
              className="upload-icon"
            />
          )}
          <input
            type="file"
            id="commercialRegistrationNumberAttachment"
            style={{ display: "none" }}
            onChange={(e) => handleCommercialRegistrationAttachmentChange(e)}
            name="commercialRegistrationNumberAttachment"
            accept=".png,.jpg,.jpeg,.pdf,.doc,.docx"
          // disabled={
          //   values?.commercialRegistrationNumberAttachment ? true : false
          // }
          />
        </div>

        <div className="upload-description ms-3">
          <p>{t("Commercial Registration Document")} <span className="text-danger">*</span></p>
          {values?.commercialRegistrationNumberAttachment ? (
            <>
              <p className="mb-0">{t("File Uploaded Successfully")}</p>
              <div className="d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-link text-decoration-none"
                  style={{ padding: 0 }}
                  onClick={() =>
                    handleViewDocument(
                      t("Commercial Registration Document"),
                      commercialRegistrationNumberAttachments,
                      commercialRegistrationNumberAttachmentType,
                      values?.commercialRegistrationNumberAttachment
                    )
                  }
                >
                  {t("View Document")}
                </button>
                <span className="text-muted">|</span>
                <button
                  type="button"
                  className="btn text-danger text-underline-none"
                  style={{ padding: 0 }}
                  onClick={handleRemoveCommercialDocument}
                >
                  {t("Remove Document")}
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="mb-0">{t("Maximum file size: Up to 2 MB")}</p>
              <p className="mb-0">
                {t("Supported format: .png, .jpg, .pdf .doc")}
              </p>
            </>
          )}
        </div>
      </div>
      {touched.commercialRegistrationNumberAttachment &&
        errors.commercialRegistrationNumberAttachment && (
          <div className="text-danger">
            {errors.commercialRegistrationNumberAttachment}
          </div>
        )}

      <div className="mb-3">
        <input
          type="text"
          className="form-control"
          name="taxIdentifictionNumber"
          placeholder={`${t("Tax identification number")}`}
          required
          onChange={handleChange}
          value={values.taxIdentifictionNumber}
          onBlur={handleBlur}
        />
        {/* {touched.taxIdentifictionNumber && errors.taxIdentifictionNumber && (
          <div className="text-danger">{errors.taxIdentifictionNumber}</div>
        )} */}
      </div>

      <div className="upload-container d-flex align-items-center mb-3">
        <div
          className="upload-box"
          onClick={() =>
            document.getElementById("taxIdentifictionNumberAttachment").click()
          }
        >

          {values?.taxIdentifictionNumberAttachment && !taxIdentifictionNumberAttachments ? (
            <img
              src={values?.taxIdentifictionNumberAttachment}
              alt={t("Upload Icon")}
              className="upload-icon"
            />
          ) : taxIdentifictionNumberAttachmentType && !taxIdentifictionNumberAttachmentType.startsWith("image/") ? (
            <a href={taxIdentifictionNumberAttachments} target="_blank" rel="noopener noreferrer">
              <img
                src={document_img}
                alt="Document Icon"
                className="upload-icon"
                style={{ cursor: "pointer" }}
              />
            </a>
          ) : (
            <img
              src={taxIdentifictionNumberAttachments ? taxIdentifictionNumberAttachments : uploadImage}
              alt={t("Upload Icon")}
              className="upload-icon"
            />
          )}
          <input
            type="file"
            id="taxIdentifictionNumberAttachment"
            style={{ display: "none" }}
            onChange={(e) => handleTaxIdentificationAttachmentChange(e)}
            name="taxIdentifictionNumberAttachment"
            // disabled={values?.taxIdentifictionNumberAttachment ? true : false}
            accept=".png,.jpg,.jpeg,.pdf,.doc,.docx"
          />
        </div>
        <div className="upload-description ms-3">
          <p>{`${t("Tax Identification Document")}`}</p>
          {values?.taxIdentifictionNumberAttachment ? (
            <>
              <p className="mb-0">{t("File Uploaded Successfully")}</p>
              <div className="d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-link text-decoration-none"
                  style={{ padding: 0 }}
                  onClick={() =>
                    handleViewDocument(
                      t("Tax Identification Document"),
                      taxIdentifictionNumberAttachments,
                      taxIdentifictionNumberAttachmentType,
                      values?.taxIdentifictionNumberAttachment
                    )
                  }
                >
                  {t("View Document")}
                </button>
                <span className="text-muted">|</span>
                <button
                  type="button"
                  className="btn text-danger text-underline-none"
                  style={{ padding: 0 }}
                  onClick={handleRemoveTaxDocument}
                >
                  {t("Remove Document")}
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="mb-0">{t("Maximum file size: Up to 2 MB")}</p>
              <p className="mb-0">
                {t("Supported format: .png, .jpg, .pdf .doc")}
              </p>
            </>
          )}
          <p id="fileName1" className="mt-2 text-muted"></p>
        </div>
      </div>
      {touched.taxIdentifictionNumberAttachment &&
        errors.taxIdentifictionNumberAttachment && (
          <div className="text-danger">
            {errors.taxIdentifictionNumberAttachment}
          </div>
        )}

      <PreviewModal
        show={previewModal.show}
        onHide={() => setPreviewModal({ ...previewModal, show: false })}
        title={previewModal.title}
        fileUrl={previewModal.fileUrl}
        fileType={previewModal.fileType}
      />
    </>
  );
};

export default Step3;
